import { useCallback, useEffect, useMemo, useState } from "react";
import type { StaffMember } from "./useStaff";

type EditorFormState = {
  routeKey: string;
  form: StaffMember;
  hydrated: boolean;
};

/**
 * Resolve an editor form from the asynchronously hydrated roster exactly once
 * per route. Subsequent roster refreshes must not replace edits already made in
 * the form; changing to another route intentionally starts a fresh resolution.
 */
export function useStaffEditorForm({
  id,
  isNew,
  members,
  createEmpty,
}: {
  id?: string;
  isNew: boolean;
  members: StaffMember[];
  createEmpty: () => StaffMember;
}) {
  const routeKey = isNew || !id ? "new" : `existing:${id}`;
  const persistedMember = useMemo(
    () => (!isNew && id ? members.find((member) => member.id === id) : undefined),
    [id, isNew, members],
  );
  const [state, setState] = useState<EditorFormState>(() => ({
    routeKey,
    form: persistedMember ?? createEmpty(),
    hydrated: isNew || !id || !!persistedMember,
  }));

  useEffect(() => {
    setState((current) => {
      if (current.routeKey !== routeKey) {
        return {
          routeKey,
          form: persistedMember ?? createEmpty(),
          hydrated: isNew || !id || !!persistedMember,
        };
      }
      if (current.hydrated || isNew || !id || !persistedMember) return current;
      return { routeKey, form: persistedMember, hydrated: true };
    });
  }, [createEmpty, id, isNew, persistedMember, routeKey]);

  const setForm = useCallback<React.Dispatch<React.SetStateAction<StaffMember>>>((next) => {
    setState((current) => ({
      ...current,
      form: typeof next === "function" ? next(current.form) : next,
    }));
  }, []);

  return {
    form: state.form,
    setForm,
    persistedMember,
    hydrated: state.routeKey === routeKey && state.hydrated,
  };
}
